import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";

import type { Graphics as GraphicsType } from "pixi.js";
import { Container, EventMode, FederatedPointerEvent, Graphics, Text, Texture } from "pixi.js";
import { extend } from "@pixi/react";

import { IPixCircle, IPixPolygon } from "../types";

extend({ Container });
extend({ Graphics });
extend({ Text });

interface PixBimCircleProps {
  circle: IPixCircle;
  onPointerDown?: (
    e: FederatedPointerEvent,
    item: IPixPolygon | IPixCircle,
    coordinates: [number, number]
  ) => void;
  onClick?: (e: FederatedPointerEvent, circle: IPixCircle) => void;
  onRightClick?: (e: FederatedPointerEvent, id: string) => void;
  onPolygonHover?: (e: FederatedPointerEvent, tooltipText?: string) => void;
}

export interface PixBimPolygonType {
  setNotPreSelected: () => void;
  setPreSelected: () => void;
}

const PixBimCircle = forwardRef<PixBimPolygonType, PixBimCircleProps>(
  ({ onClick, onRightClick, onPolygonHover, onPointerDown, circle }, ref) => {
    const { color, text, opacity = 1, position, radius, isClickable = false } = circle;
    const scaledRadius = radius * 100;
    const [fillColor, setFillColor] = useState(color || "#fafafa");
    const graphicsRef = useRef<Graphics | null>(null);

    const { values: textValues, color: textColor = "#000", show: textShow } = text || {};

    useEffect(() => {
      const newFillColor = color || "#fafafa";
      setFillColor(newFillColor);
    }, [color]);

    useImperativeHandle(ref, () => {
      return {
        setNotPreSelected: () => {
          drawAgain();
        },
        setPreSelected: () => {
          drawAgain("#e1eaf5");
        },
      };
    });
    const drawAgain = (color = fillColor) => {
      const g = graphicsRef.current;
      if (!g) {
        return;
      }
      const texture: Texture = Texture.WHITE;
      texture.source.scaleMode = "nearest";
      g.clear();
      g.fill({ texture, color });
      // const pointsFlat = points.flat(1);
      // g.poly(pointsFlat);
      g.eventMode = "static";
      g.fill();
    };

    const draw = useCallback(
      (g: GraphicsType) => {
        const texture: Texture = Texture.WHITE;
        const [x, y] = position;
        texture.source.scaleMode = "nearest";
        g.clear();
        g.fill({ texture, color: fillColor });
        g.circle(x, y, scaledRadius);
        g.eventMode = "static";
        g.fill({ texture, color: fillColor, alpha: opacity });
      },
      [position, scaledRadius, fillColor, opacity]
    );

    const onProxyClick = (e: FederatedPointerEvent) => {
      if (onClick) {
        onClick(e, circle);
      }
    };

    const sizeConstant = (scaledRadius * 2 > 250 ? 250 : scaledRadius * 2) / 6;

    const interactiveOptions = {
      eventMode: "static" as EventMode,
      ...(onClick && { onClick: onProxyClick }),
    };

    const containerOptions = {
      ...(isClickable && interactiveOptions),
      ...(!isClickable && { eventMode: "none" as EventMode }),
    };
    return (
      <container {...containerOptions}>
        <graphics ref={graphicsRef} draw={draw} />
        {textShow && textValues && (
          <pixiText
            eventMode="none"
            text={textValues?.join("\n")}
            style={{ fontSize: sizeConstant, align: "center", fill: textColor }}
            anchor={{ x: 0.5, y: 0.5 }}
            zIndex={1}
            position={{ x: position[0], y: position[1] }}
          />
        )}
      </container>
    );
  }
);

export default PixBimCircle;
